import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  forwardRef,
  OnChanges,
  ViewChild,
  ChangeDetectorRef,
} from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import { ObjectUtils } from "@bfx/primeng/utils";

// BFX
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BfxInputControlStateEnum } from "./models/bfx-interface-controls.enums";
import { ActiveUser, ActiveUserProfilePromiseService } from "shared/cubiq-services";

// models
import { BfxOrderEntryTimeControlModel } from "./models/bfx-interface-controls.models";
import { TimeZoneImpl } from "shared/cubiq-model-multi-asset";
import { BfxAutocompleteGenericModel } from "./models/bfx-interface-controls.models";

export const TIME_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BfxTimeInputComponent),
  multi: true,
};

export const VALIDATE_TIME_PATTERN: any = new RegExp("^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$");

const EMPTY_TZ_GENERIC_MODEL = new BfxAutocompleteGenericModel(null, "");
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { OETimeMachineService } from "../order-entry/services/basic/oe-time-machine.service";
import { BfxAutoCompleteComponent } from "../bfx-element-library/bfx-autocomplete.component";
import { BfxTimeEditComponent } from "../bfx-element-library/bfx-time-edit.component";

@Component({
  selector: "bfx-time-input",
  templateUrl: "./html/bfx-time-input.html",
  providers: [TIME_CONTROL_VALUE_ACCESSOR, ObjectUtils],
  standalone: false,
})
export class BfxTimeInputComponent implements OnInit, OnChanges, ControlValueAccessor {
  @ViewChild("inputMask") inputMask: BfxTimeEditComponent;

  @ViewChild("timezoneDropdown") timezoneDropdown: BfxAutoCompleteComponent;

  @Input("nativeDropdown")
  public nativeDropdown: boolean = false;

  @Input("userZone") input_setTimezoneToUserZone: boolean = true;

  @Input("showValidation") input_showValidation: boolean = true;

  @Input("suspend") input_suspend: boolean = false;

  @Input("fillTimeOnZone") fillTimeOnZone: boolean = false;

  @Input("adjustDropdownDir") input_adjustDropdown: boolean = false;

  @Input("label") input_label: string;
  _skipDefaults: boolean = false;

  @Input("skipDefaults")
  get skipDefaults() {
    return this._skipDefaults;
  }

  set skipDefaults(skipDefaults) {
    this._skipDefaults = skipDefaults;

    if (skipDefaults) {
      this.data.placeholder = skipDefaults ? "__:__" : "00:00";
    }
  }
  @Output() blur: EventEmitter<any> = new EventEmitter<any>();

  @Output() focus: EventEmitter<any> = new EventEmitter<any>();

  @Input()
  get value() {
    return this.data.value;
  }

  set value(value) {
    this.data.value = value;

    if (value != null) {
      this.data.timeValue = value.timeValue;
      this.data.timezoneValue = value.timezoneValue;
    }

    this.cd.markForCheck();
  }

  // From external change state
  input_externalState: BfxInputControlStateEnum;

  @Input()
  get externalState() {
    return this.input_externalState;
  }

  set externalState(value: any) {
    this.input_externalState = value;
  }

  public autoClear: boolean = false;
  /***************************************/
  /* DATA */
  public config: any = {
    showValidation: true, // indicates whether to show or hide validation at the bottom
    suspend: false, //
  };

  public data: any = {
    value: null, // BfxOrderEntryTimeControlModel
    label: null, // string
    state: BfxInputControlStateEnum.INACTIVE, // BfxInputControlStateEnum
    timeValue: null, //string
    timezoneValue: null, // BfxAutocompleteGenericModel
    timezoneDatasetFiltered: null, // Array<BfxAutocompleteGenericModel>
    timezoneDataset: null, // Array<BfxAutocompleteGenericModel>
    timezoneImplDataset: null, // Array<TimeZoneImpl>
    userTimezoneCode: null, // string
    userTimeZoneModel: null, // TimeZoneImpl
    locked: false, // boolean
    placeholder: null,
  };

  private menuScrollHeight: number = 150;

  public inputModified: boolean = false;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */
  constructor(
    private activeUserService: ActiveUserProfilePromiseService,
    private _oeTimeMachine: OETimeMachineService,
    public objectUtils: ObjectUtils,
    protected cd: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.initializeOnInit();
  }

  ngOnChanges() {
    this.initializeOnChanges();
  }

  /**********************************************************************/
  /* INITIALIZATION */
  protected initializeOnInit(): void {
    this.loadActiveUserTimezone();
    this.loadTimezones();
    this.initializeOnChanges();
  }

  protected initializeOnChanges(): void {
    if (this.input_externalState != null) {
      this.data.state = this.input_externalState;
    }

    if (
      this.input_label != null &&
      typeof this.input_label === "string" &&
      this.input_label.length > 0
    ) {
      this.data.label = this.input_label;
    }

    if (this.input_showValidation != null) {
      this.config.showValidation = this.input_showValidation;
    }

    if (this.input_suspend != null) {
      this.config.suspend = this.input_suspend;
      if (this.config.suspend) {
        this.fillInTheBlanks();
        // setTimeout(this.onBlur(null),250);
      }
    }
  }
  // User Timezone
  private loadActiveUserTimezone(): void {
    if (this.input_setTimezoneToUserZone) {
      this.activeUserService.getActiveUserData().then((user: ActiveUser) => {
        if (user != null && user instanceof ActiveUser) {
          this.data.userTimezoneCode = user.timeZoneCd;
          if (this.data.timezoneImplDataset && this.data.userTimezoneCode) {
            let theTz: CubiqTS.TimeZoneImpl = this.data.timezoneImplDataset.find(
              (tz) => tz.id === this.data.userTimezoneCode,
            );
            if (theTz) {
              this.data.timezoneValue = new BfxAutocompleteGenericModel(
                theTz.id,
                this.getTimezoneLabel(theTz),
              );
              if (this.data.value == null || this.data.value == "") {
                this.data.value = new BfxOrderEntryTimeControlModel();
              }
              this.data.value.timezoneValue = this.data.timezoneValue;
              this.data.value.timezoneModel = theTz;
              this.data.userTimeZoneModel = theTz;
            }
          }
        } else {
          logger.out.error("ERROR: TimeInput.initializeOnInit(): ActiveUser not found!");
        }
      });
    }
  }

  // Timezone lookup // Utility
  private loadTimezones(): void {
    this.data.timezoneDataset = new Array<BfxAutocompleteGenericModel>();
    this.data.timezoneImplDataset = this._oeTimeMachine.getTimeZoneImpls();
    if (
      this.data.timezoneImplDataset != null &&
      Array.isArray(this.data.timezoneImplDataset) &&
      this.data.timezoneImplDataset.length > 0
    ) {
      if (this.skipDefaults) {
        this.data.timezoneDataset.push(EMPTY_TZ_GENERIC_MODEL);
      }
      this.data.timezoneImplDataset.forEach((tzItem: CubiqTS.TimeZoneImpl) => {
        if (this.input_setTimezoneToUserZone && tzItem.id === this.data.userTimezoneCode) {
          this.data.timezoneValue = new BfxAutocompleteGenericModel(tzItem.id, tzItem.id); //{ label: item.id, value: item.id };
          if (this.data.value == null) {
            this.data.value = new BfxOrderEntryTimeControlModel();
          }
          this.data.value.timezoneValue = this.data.timezoneValue;
          this.data.value.timezoneModel = tzItem; //this.data.timezoneImplDataset.find((item)=>{ return item.id === this.data.timezoneValue;});
          this.data.userTimeZoneModel = tzItem;
        }
        this.data.timezoneDataset.push(
          new BfxAutocompleteGenericModel(tzItem.id, this.getTimezoneLabel(tzItem)),
        );
      });
      if (this.config.suspend) {
        this.onChange(null);
      }
    } else {
      logger.out.error("ERROR: TimeInput.loadTimezones(): Timezones load failed or empty array!");
    }
  }

  public filterTimezones(event): void {
    let result: Array<BfxAutocompleteGenericModel> = [];
    let searchToken: string = event.query.toLowerCase();

    if (this.data.timezoneDataset != null && this.data.timezoneDataset.length > 0) {
      // client is selected, local filtering
      for (let item of this.data.timezoneDataset) {
        if (item.label.toLowerCase().indexOf(searchToken) >= 0) {
          result.push(item);
        }
        if (this.config.resultsToShow > 0 && result.length >= this.config.resultsToShow) {
          this.data.timezoneDatasetFiltered = result;
          return;
        }
      }
    }

    this.data.timezoneDatasetFiltered = result;
    if (this.input_adjustDropdown) {
      setTimeout(this.adjustDropDown.bind(this, event, result), 30);
    }
  }

  /**********************************************************************/
  /* VALUE ACCESSOR */

  onModelChange: Function = () => {};

  onModelTouched: Function = () => {};

  writeValue(value: any): void {
    this.data.value = value;
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  /**********************************************************************/
  /* LOAD */
  public loadInputControl(valueModel: BfxOrderEntryTimeControlModel): void {
    if (!this.skipDefaults || valueModel != null) {
      this.data.value = valueModel;
      this.data.timeValue = valueModel.timeValue;
      this.data.timezoneValue = valueModel.timezoneValue;
    } else {
      this.data.value = null;
      this.data.userTimeZoneModel = null;
      this.data.userTimezoneCode = null;
      this.data.timezoneValue = EMPTY_TZ_GENERIC_MODEL;
    }
    this.onValuesChange();
  }

  /**********************************************************************/
  /* RESET */

  public resetInputControl(): void {
    if (this.skipDefaults) {
      this.data.timeValue = null;
      this.data.userTimeZoneModel = null;
      this.data.userTimezoneCode = null;
      this.data.timezoneValue = EMPTY_TZ_GENERIC_MODEL;
    } else {
      this.data.timeValue = "00:00";
      if (this.data.userTimezoneCode != null && this.data.userTimezoneCode !== "") {
        let timezoneImplModel = this.data.timezoneImplDataset.find((item) => {
          return item.id === this.data.userTimezoneCode;
        });
        this.data.userTimeZoneModel = timezoneImplModel;
        this.data.timezoneValue = new BfxAutocompleteGenericModel(
          timezoneImplModel.id,
          this.getTimezoneLabel(timezoneImplModel),
        );
      }
    }

    this.onValuesChange();

    this.data.state = BfxInputControlStateEnum.INACTIVE;
    this.data.locked = false;
    this.externalState = null;
    //
    this.inputModified = false;
    //
    this.initializeOnChanges();
  }
  /**********************************************************************/
  /* LOCKING */
  public lockInputControl(): void {
    this.data.locked = true;
  }

  public unlockInputControl(): void {
    this.data.locked = false;
  }
  /**********************************************************************/
  /* HANDLERS */
  public onChange(event: any): void {
    this.assignValuesToModel();
    this.onModelChange(this.data.value);
  }

  public onFocus($event: any): void {
    if (!this.inputModified) {
      this.inputModified = true;
      $event.firstFocus = true;
    } else {
      $event.firstFocus = false;
    }
    if (this.data.state === BfxInputControlStateEnum.INACTIVE)
      this.data.state = BfxInputControlStateEnum.ACTIVE;
    this.onModelTouched();
    this.focus.emit($event);
  }

  public onBlur($event: any): void {
    this.onValuesChange();
    if (typeof $event != "string") {
      $event.internalState = this.data.state;
    }
    this.blur.emit($event);
  }

  /**********************************************************************/
  /* INTERFACE API */
  public setModified(): void {
    this.inputModified = true;
    this.fillInTheBlanks();
    this.onChange(null);
  }

  /**********************************************************************/
  /* INTERNAL */
  protected onValuesChange(): void {
    this.cd.markForCheck();

    this.assignValuesToModel();

    this.onChange(null);
  }

  protected assignValuesToModel(): void {
    if (this.data.value == null || this.data.value == "")
      this.data.value = new BfxOrderEntryTimeControlModel();

    if (
      this.data.value.timeValue !== this.data.timeValue ||
      this.data.value.timezoneValue == null ||
      (this.data.value.timezoneValue != null && this.data.timezoneValue === null) ||
      this.data.value.timezoneValue.value !== this.data.timezoneValue.value ||
      this.data.value.timezoneModel == null ||
      this.data.value.timezoneModel.id !== this.data.timezoneValue.value
    ) {
      this.data.value.timeValue = this.data.timeValue;
      this.data.value.timezoneValue = this.data.timezoneValue;
      if (this.data.timezoneValue) {
        let timezoneImplModel = this.data.timezoneImplDataset.find((item) => {
          if (this.data.timezoneValue.id != null) {
            return item.id === this.data.timezoneValue.id;
          } else {
            return item.id === this.data.timezoneValue.value;
          }
        });

        if (timezoneImplModel != null) {
          this.data.value.timezoneModel = timezoneImplModel;
          if (
            this.fillTimeOnZone === true &&
            this.data.value.timeValue == null &&
            this.data.timeValue == null
          ) {
            this.data.timeValue = this.data.value.timeValue = this.inputMask.showSeconds
              ? "00:00:00"
              : "00:00";
          }
        } else {
          this.data.value.timezoneModel = null;
          if (this.fillTimeOnZone === true) {
            this.data.timeValue = this.data.value.timeValue = null;
          }
          // logger.out.error(`ERROR: BfxTimeInputComponent.loadInputControl(): Can not find TimeZoneImpl model to load for '${this.data.timezoneValue}'`);
        }
      }
    }
  }

  public validate(): boolean {
    this.fillInTheBlanks();
    if (this.data.timeValue != null && this.data.timezoneValue != null) {
      // Time validation
      let timeValid: boolean = VALIDATE_TIME_PATTERN.test(this.data.timeValue);
      // Timezone validation
      let timezoneValid: boolean = false;
      if (typeof this.data.timezoneValue === "string" && this.data.timeValue !== "") {
        this.data.timezoneValue = new BfxAutocompleteGenericModel(
          this.data.timezoneValue.toUpperCase(),
          this.data.timezoneValue.toUpperCase(),
        );
      }

      if (this.data.timezoneValue instanceof BfxAutocompleteGenericModel) {
        timezoneValid =
          this.data.timezoneImplDataset.filter((item) => {
            return item.id === this.data.timezoneValue.value;
          }).length > 0;
      }
      // resolution
      if (timeValid && timezoneValid) {
        // || this.data.timeValue === ''
        this.data.state = BfxInputControlStateEnum.VALID;
        this.externalState = null;
        return true;
      } else {
        this.data.state = BfxInputControlStateEnum.INVALID;
        this.externalState = null;
        return false;
      }
    } else {
      this.data.state = BfxInputControlStateEnum.INACTIVE;
      this.externalState = null;
      return true;
    }
  }

  protected fillInTheBlanks() {
    if (this.skipDefaults) {
      this.data.timeValue = "__:__";
      this.data.placeholder = this.skipDefaults ? "__:__" : "00:00";
    } else if (this.data.timeValue == null || this.data.timeValue === "") {
      this.data.timeValue = "00:00";
    } else if (this.data.timeValue != null && this.data.timeValue.length > 0) {
      this.data.timeValue = this.data.timeValue.replace(/_/g, "0");
    }
  }
  /* ************************************************************************ */
  /* Dropdown direction fix  */
  private adjustDropDown(event: any, items: Array<any>): void {
    let element: any = event.originalEvent.target;

    let componentElement: any = this.getAutoSuggestComponentElement(element);
    let dropDownElement: any = this.getDropDownElement(componentElement);
    let scrollBodyElement: any = this.getScrollBodyElement(componentElement);

    let scrollOffset: number = scrollBodyElement.scrollTop;

    let height: number = Math.min(items.length * 22 + 15, this.menuScrollHeight);
    // dropDownElement.style.height = height + 'px';

    let panelHeight: number = scrollBodyElement.offsetHeight;

    if (componentElement.offsetTop - scrollOffset + height > panelHeight) {
      dropDownElement.style.top = null;
      dropDownElement.style.bottom = "20px";
    } else {
      dropDownElement.style.bottom = null;
      dropDownElement.style.top = "20px";
    }
  }

  protected getAutoSuggestComponentElement(eventSourceElement: any): any {
    if (eventSourceElement.nodeName.toLowerCase() !== "bfx-autocomplete") {
      return this.getAutoSuggestComponentElement(eventSourceElement.parentElement);
    }

    return eventSourceElement;
  }

  protected getScrollBodyElement(eventSourceElement: any): any {
    if (
      typeof eventSourceElement?.className === "string" &&
      eventSourceElement.className.toLowerCase() !==
        "order-entry__panel-financials__additional__pane"
    ) {
      return this.getScrollBodyElement(eventSourceElement.parentElement);
    }

    return eventSourceElement;
  }

  protected getDropDownElement(autoSuggestComponentElement: any): any {
    return autoSuggestComponentElement.children[0].children[0].children[2];
  }

  protected getTimezoneLabel(tzItem: CubiqTS.TimeZoneImpl): string {
    let tzLabel: string = tzItem.id;
    if (typeof tzLabel === "string" && tzLabel.length > 4) {
      tzLabel = tzLabel.substring(0, 4);
    }
    return tzLabel;
  }
}
